import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { subscriptionAPI } from '../services/api'

declare global {
  interface Window {
    Cashfree: any
  }
}

const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: '₹999',
    color: '#6b7280',
    features: [
      'Top 5 anomaly alerts',
      'Telegram delivery',
      'Morning market scan',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '₹2,499',
    color: '#00d4ff',
    highlight: true,
    features: [
      'Top 15 anomalies',
      'Wavelet analysis',
      'Strategy hints',
      'Signal history (5 days)',
      'Accuracy analytics',
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    price: '₹4,999',
    color: '#f59e0b',
    features: [
      'Everything in Pro',
      'Priority Telegram alerts',
      'Custom watchlist',
      'Onboarding call',
    ],
  },
]

export default function Upgrade() {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const handleUpgrade = async (planId: string) => {
    setLoading(planId)
    setError('')
    try {
      // Create order on backend
      const res = await subscriptionAPI.createOrder(planId)
      const { payment_session_id, order_id } = res.data

      // Initialize Cashfree
      const cashfree = await window.Cashfree({
        mode: 'production',
      })

      // Open Cashfree checkout
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: '_self',
      })

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.')
      setLoading(null)
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1 style={styles.title}>Upgrade Your Plan</h1>
        <p style={styles.subtitle}>
          Unlock the full power of NSE F&O intelligence
        </p>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.plans}>
        {plans.map(plan => (
          <div
            key={plan.id}
            style={{
              ...styles.card,
              border: plan.highlight ? '2px solid #00d4ff' : '1px solid #1f2937',
            }}
          >
            {plan.highlight && (
              <div style={styles.badge}>Most Popular</div>
            )}
            <h2 style={{ color: plan.color, margin: '0 0 8px 0' }}>{plan.name}</h2>
            <div style={styles.price}>
              {plan.price}
              <span style={styles.per}>/month</span>
            </div>
            <ul style={styles.features}>
              {plan.features.map(f => (
                <li key={f} style={styles.feature}>✓ {f}</li>
              ))}
            </ul>
            <button
              style={{
                ...styles.btn,
                background: loading === plan.id ? '#374151' : plan.color,
                cursor: loading === plan.id ? 'not-allowed' : 'pointer',
              }}
              onClick={() => handleUpgrade(plan.id)}
              disabled={loading === plan.id}
            >
              {loading === plan.id ? 'Processing...' : `Upgrade to ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      <p style={styles.disclaimer}>
        Payments processed securely via Cashfree. Cancel anytime.
        ⚠️ AITradeMinds is research intelligence only — not SEBI registered investment advice.
      </p>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: '#0a0e1a',
    minHeight: '100vh',
    color: '#fff',
    padding: '24px 16px',
  },
  header: { textAlign: 'center', marginBottom: 40 },
  backBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #374151',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    marginBottom: 24,
  },
  title: { fontSize: 28, fontWeight: 800, margin: '0 0 8px 0' },
  subtitle: { color: '#6b7280', fontSize: 15 },
  error: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 24,
    textAlign: 'center',
  },
  plans: {
    display: 'flex',
    justifyContent: 'center',
    gap: 20,
    flexWrap: 'wrap' as const,
    maxWidth: 900,
    margin: '0 auto',
  },
  card: {
    background: '#111827',
    borderRadius: 12,
    padding: 28,
    width: 260,
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#00d4ff',
    color: '#000',
    padding: '4px 16px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 700,
  },
  price: { fontSize: 32, fontWeight: 800, color: '#fff', margin: '16px 0 20px' },
  per: { fontSize: 14, color: '#6b7280', fontWeight: 400 },
  features: { listStyle: 'none', padding: 0, margin: '0 0 24px 0' },
  feature: { color: '#9ca3af', padding: '6px 0', fontSize: 14 },
  btn: {
    width: '100%',
    padding: '13px',
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 15,
    color: '#000',
  },
  disclaimer: {
    textAlign: 'center',
    color: '#374151',
    fontSize: 12,
    marginTop: 40,
    maxWidth: 600,
    margin: '40px auto 0',
  },
}